import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import ConfirmModal from "../components/ConfirmModal";
import Toast from "../components/Toast";
import Footer from "../components/Footer";

import { fetchUserProfile, fetchUserTocs, updateUserProfile } from "../services/api";

import "../style/profile.css";

type ProjectStatus = "draft" | "published";

type UserProfile = {
  userId: number | string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  organisation: string;
  roleDescription?: string;
  role?: string;
  usagePurpose?: string;
  usage_purpose?: string;
  avatarUrl?: string | null;
  displayName: string;
  createdAt: string;
};

type ProjectSummary = {
  projectId: string;
  projectName: string;
  status?: string;
  updatedAt?: string;
  createdAt?: string;
};

type EditForm = {
  firstName: string;
  lastName: string;
  organisation: string;
  role: string;
  usagePurpose: string;
  username: string;
  avatarUrl: string | null;
};

type ToastState = {
  message: string;
  type: "success" | "error" | "warning" | "info";
} | null;

type TimelineItem = {
  title: string;
  description: string;
  tone: "accent" | "success" | "neutral";
};

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];

const MAX_AVATAR_SIZE = 2 * 1024 * 1024;

const CURRENT_AVATAR_KEY = "qfo-current-profile-avatar";
const CURRENT_PROFILE_KEY = "qfo-current-profile-data";
const PROFILE_UPDATED_EVENT = "qfo-profile-updated";

const normaliseProjectStatus = (status?: string | null): ProjectStatus => {
  const value = String(status || "").trim().toLowerCase();

  if (
    value === "completed" ||
    value === "complete" ||
    value === "published" ||
    value === "done"
  ) {
    return "published";
  }

  return "draft";
};

const formatProjectStatusLabel = (status?: string | null): string => {
  const normalised = normaliseProjectStatus(status);
  return normalised === "published" ? "Published" : "Draft";
};

const formatDate = (date?: string, fallback = "—"): string => {
  if (!date) return fallback;

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return fallback;

  return parsedDate.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (date?: string, fallback = "—"): string => {
  if (!date) return fallback;

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return fallback;

  return parsedDate.toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getInitials = (profile: UserProfile | null): string => {
  const first = profile?.firstName?.[0] || profile?.displayName?.[0] || "U";
  const second = profile?.lastName?.[0] || profile?.username?.[0] || "";

  return `${first}${second}`.toUpperCase();
};

const getAvatarStorageKey = (profile: UserProfile): string => {
  return `qfo-profile-avatar-${profile.userId || profile.email}`;
};

const applyStoredAvatar = (profile: UserProfile): UserProfile => {
  try {
    const userSpecificKey = getAvatarStorageKey(profile);
    const userSpecificAvatar = localStorage.getItem(userSpecificKey);

    if (userSpecificAvatar !== null) {
      return {
        ...profile,
        avatarUrl: userSpecificAvatar || null,
      };
    }

    const sharedAvatar = localStorage.getItem(CURRENT_AVATAR_KEY);

    if (sharedAvatar !== null) {
      return {
        ...profile,
        avatarUrl: sharedAvatar || null,
      };
    }
  } catch {
    // Ignore localStorage errors.
  }

  return profile;
};

const persistAvatarChoice = (
  profile: UserProfile,
  avatarUrl: string | null
): void => {
  try {
    const userSpecificKey = getAvatarStorageKey(profile);

    localStorage.setItem(userSpecificKey, avatarUrl || "");
    localStorage.setItem(CURRENT_AVATAR_KEY, avatarUrl || "");

    const profilePayload = {
      userId: profile.userId,
      email: profile.email,
      username: profile.username,
      firstName: profile.firstName,
      lastName: profile.lastName,
      organisation: profile.organisation,
      role:
        profile.roleDescription ||
        profile.role ||
        "",
      usagePurpose: profile.usagePurpose || profile.usage_purpose || "",
      displayName: profile.displayName,
      avatarUrl: avatarUrl || null,
    };

    localStorage.setItem(CURRENT_PROFILE_KEY, JSON.stringify(profilePayload));

    window.dispatchEvent(
      new CustomEvent(PROFILE_UPDATED_EVENT, {
        detail: {
          avatarUrl: avatarUrl || null,
          profile: profilePayload,
        },
      })
    );
  } catch {
    // Ignore localStorage errors.
  }
};

const readFileAsDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () =>
      reject(new Error("Could not read the selected image."));

    reader.readAsDataURL(file);
  });
};

const getProjectDate = (project: ProjectSummary): string => {
  return project.updatedAt || project.createdAt || "";
};

const mapProject = (project: any): ProjectSummary => {
  return {
    projectId: String(project.projectId || project.id || ""),
    projectName:
      project.projectName ||
      project.tocData?.programName ||
      project.projectTitle ||
      project.tocData?.projectTitle ||
      "Untitled project",
    status: project.status || "draft",
    updatedAt: project.updatedAt || project.modifiedAt || project.lastModified,
    createdAt: project.createdAt || project.createdDate,
  };
};

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const avatarInputRef = useRef<HTMLInputElement | null>(null);

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const [projects, setProjects] = useState<ProjectSummary[]>([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);

  const [toast, setToast] = useState<ToastState>(null);

  const [editForm, setEditForm] = useState<EditForm>({
    firstName: "",
    lastName: "",
    organisation: "",
    role: "",
    usagePurpose: "",
    username: "",
    avatarUrl: null,
  });

  const syncEditForm = (profileData: UserProfile) => {
    setEditForm({
      firstName: profileData.firstName || "",
      lastName: profileData.lastName || "",
      organisation: profileData.organisation || "",
      role:
        profileData.roleDescription ||
        profileData.role ||
        "",
      usagePurpose:
        profileData.usagePurpose || profileData.usage_purpose || "",
      username: profileData.username || "",
      avatarUrl: profileData.avatarUrl || null,
    });
  };

  const loadProfileData = async () => {
    const profileData = await fetchUserProfile();
    const profileWithAvatar = applyStoredAvatar(profileData);

    setUserProfile(profileWithAvatar);
    syncEditForm(profileWithAvatar);

    persistAvatarChoice(profileWithAvatar, profileWithAvatar.avatarUrl || null);
  };

  const loadProjectData = async () => {
    try {
      const response = await fetchUserTocs();
      const rawProjects = response?.data?.projects || response?.projects || [];

      const mappedProjects = Array.isArray(rawProjects)
        ? rawProjects.map(mapProject).filter((project) => project.projectId)
        : [];

      setProjects(mappedProjects);
    } catch (projectError) {
      console.warn("Could not fetch project activity:", projectError);
      setProjects([]);
    }
  };

  const refreshProfileDashboard = async () => {
    try {
      setRefreshing(true);
      setError(null);
      setSuccessMessage(null);

      await Promise.all([loadProfileData(), loadProjectData()]);

      setToast({
        message: "Profile dashboard refreshed.",
        type: "success",
      });
    } catch (err: any) {
      const message = err?.message || "Failed to refresh profile dashboard";

      setError(message);
      setToast({
        message,
        type: "error",
      });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const initialiseProfilePage = async () => {
      try {
        setLoading(true);
        setError(null);

        await Promise.all([loadProfileData(), loadProjectData()]);
      } catch (err: any) {
        setError(err?.message || "Failed to load user profile");
        console.error("Error loading profile page:", err);
      } finally {
        setLoading(false);
      }
    };

    initialiseProfilePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmProfileRefresh = async () => {
    setShowRefreshConfirm(false);
    setIsEditing(false);
    await refreshProfileDashboard();
  };

  const handleEditToggle = () => {
    if (isEditing && userProfile) {
      syncEditForm(userProfile);
    }

    setIsEditing(!isEditing);
    setError(null);
    setSuccessMessage(null);
  };

  const handleFormChange = (field: keyof EditForm, value: string | null) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleChooseAvatar = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    event.target.value = "";

    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setToast({
        message:
          "Please upload an image only. Allowed formats: JPG, PNG, WEBP, or GIF.",
        type: "error",
      });
      return;
    }

    if (file.size > MAX_AVATAR_SIZE) {
      setToast({
        message: "Image is too large. Please choose an image under 2MB.",
        type: "error",
      });
      return;
    }

    try {
      const imageDataUrl = await readFileAsDataUrl(file);

      handleFormChange("avatarUrl", imageDataUrl);
      setError(null);
      setSuccessMessage(null);
    } catch (err: any) {
      setToast({
        message: err?.message || "Could not upload image.",
        type: "error",
      });
    }
  };

  const handleRemoveAvatar = () => {
    handleFormChange("avatarUrl", null);
    setSuccessMessage(null);
    setError(null);
  };

  const handleSaveChanges = async () => {
    try {
      setUpdateLoading(true);
      setError(null);
      setSuccessMessage(null);

      const updatePayload = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        organisation: editForm.organisation,
        roleDescription: editForm.role.trim(),
        usagePurpose: editForm.usagePurpose.trim(),
        username: editForm.username,
        avatarUrl: editForm.avatarUrl,
      };

      const updatedData = await updateUserProfile(updatePayload);

      const mergedProfile: UserProfile = {
        ...(userProfile as UserProfile),
        ...updatedData,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        organisation: editForm.organisation,
        roleDescription: editForm.role.trim(),
        role: editForm.role.trim(),
        usagePurpose: editForm.usagePurpose.trim(),
        usage_purpose: editForm.usagePurpose.trim(),
        username: editForm.username,
        avatarUrl: editForm.avatarUrl,
        displayName:
          updatedData?.displayName ||
          `${editForm.firstName} ${editForm.lastName}`.trim() ||
          userProfile?.displayName ||
          "User",
      };

      persistAvatarChoice(mergedProfile, editForm.avatarUrl);

      setUserProfile(mergedProfile);
      syncEditForm(mergedProfile);
      setIsEditing(false);

      setSuccessMessage("Profile updated successfully.");
      setToast({
        message: "Profile updated successfully.",
        type: "success",
      });

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      const message = err?.message || "Failed to update profile";

      setError(message);
      setToast({
        message,
        type: "error",
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleOpenProject = (projectId: string) => {
    localStorage.setItem("projectId", projectId);
    navigate(`/projects/${projectId}`);
  };

  const dashboardStats = useMemo(() => {
    const publishedProjects = projects.filter(
      (project) => normaliseProjectStatus(project.status) === "published"
    );

    const draftProjects = projects.filter(
      (project) => normaliseProjectStatus(project.status) === "draft"
    );

    const sortedProjects = [...projects].sort((a, b) => {
      const aTime = new Date(getProjectDate(a) || 0).getTime();
      const bTime = new Date(getProjectDate(b) || 0).getTime();

      return bTime - aTime;
    });

    return {
      publishedProjects,
      draftProjects,
      sortedProjects,
      latestProject: sortedProjects[0],
      firstProject: [...projects].sort((a, b) => {
        const aTime = new Date(a.createdAt || a.updatedAt || 0).getTime();
        const bTime = new Date(b.createdAt || b.updatedAt || 0).getTime();

        return aTime - bTime;
      })[0],
    };
  }, [projects]);

  if (loading) {
    return (
      <div className="qfo-profile-dashboard-page">
        <div className="qfo-profile-page__content">
          <div className="qfo-dashboard-state-card">
            <div className="qfo-dashboard-spinner" />
            <p>Loading your profile...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error && !userProfile) {
    return (
      <div className="qfo-profile-dashboard-page">
        <div className="qfo-profile-page__content">
          <div className="qfo-dashboard-state-card">
            <h3>Something went wrong</h3>
            <p>{error}</p>
            <button
              className="qfo-primary-button"
              onClick={refreshProfileDashboard}
              disabled={refreshing}
            >
              {refreshing ? "Trying again..." : "Try Again"}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const displayedAvatar = isEditing
    ? editForm.avatarUrl
    : userProfile?.avatarUrl || null;

  const displayName =
    userProfile?.displayName ||
    `${userProfile?.firstName || ""} ${userProfile?.lastName || ""}`.trim() ||
    "User";

  const recentProjects = dashboardStats.sortedProjects;

  const timelineItems: TimelineItem[] = [
    dashboardStats.latestProject
      ? {
          title:
            normaliseProjectStatus(dashboardStats.latestProject.status) ===
            "published"
              ? "Project published"
              : "Project saved",
          description: `${
            dashboardStats.latestProject.projectName
          } · ${formatDateTime(getProjectDate(dashboardStats.latestProject))}`,
          tone:
            normaliseProjectStatus(dashboardStats.latestProject.status) ===
            "published"
              ? "success"
              : "accent",
        }
      : null,
    userProfile?.createdAt
      ? {
          title: "Account created",
          description: `Joined on ${formatDate(userProfile.createdAt)}`,
          tone: "neutral",
        }
      : null,
    dashboardStats.firstProject
      ? {
          title: "First project started",
          description: `${
            dashboardStats.firstProject.projectName
          } · ${formatDate(getProjectDate(dashboardStats.firstProject))}`,
          tone: "accent",
        }
      : null,
  ].filter(Boolean) as TimelineItem[];

  return (
    <div className="qfo-profile-dashboard-page">
      <div className="qfo-profile-page__content">
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        <header className="qfo-dashboard-header">
          <div>
            <span className="qfo-dashboard-eyebrow">Account Settings</span>
            <h1>My Profile</h1>
            <p>Manage your profile, workspace activity, and account information.</p>
          </div>

          
        </header>

        <main className="qfo-dashboard-grid">
          <section
            className="qfo-dashboard-card qfo-profile-main-card"
            aria-label="Profile details"
          >
            <div className="qfo-dash-avatar-wrapper">
              {displayedAvatar ? (
                <img
                  className="qfo-dash-avatar-image"
                  src={displayedAvatar}
                  alt="Profile"
                />
              ) : (
                <div className="qfo-dash-avatar-placeholder">
                  {getInitials(userProfile)}
                </div>
              )}
            </div>

            {isEditing && (
              <div className="qfo-dash-avatar-actions">
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleAvatarFileChange}
                  className="qfo-hidden-file-input"
                />

                <button
                  type="button"
                  className="qfo-dash-secondary-button"
                  onClick={handleChooseAvatar}
                >
                  {displayedAvatar ? "Change Photo" : "Add Photo"}
                </button>

                {displayedAvatar && (
                  <button
                    type="button"
                    className="qfo-dash-ghost-button"
                    onClick={handleRemoveAvatar}
                  >
                    Remove
                  </button>
                )}

                <p className="qfo-dash-note">JPG, PNG, WEBP, or GIF. Max 2MB.</p>
              </div>
            )}

            {successMessage && (
              <div className="qfo-dash-success-message">{successMessage}</div>
            )}

            {error && !isEditing && (
              <div className="qfo-dash-error-message">{error}</div>
            )}

            {!isEditing ? (
              <>
                <h2>{displayName}</h2>

                <p className="qfo-dash-username">
                  @{userProfile?.username || "username"}
                </p>

                <div className="qfo-dash-profile-actions">
                  <button
                    className="qfo-dash-primary-button"
                    onClick={handleEditToggle}
                  >
                    Edit Profile
                  </button>

                  <button
                    className="qfo-dash-secondary-button"
                    onClick={() => navigate("/")}
                  >
                    Workspace
                  </button>
                </div>

                <dl className="qfo-dash-detail-list">
                  <div>
                    <dt>Email</dt>
                    <dd>{userProfile?.email || "—"}</dd>
                  </div>

                  <div>
                    <dt>Organisation</dt>
                    <dd>{userProfile?.organisation || "—"}</dd>
                  </div>

                  <div>
                    <dt>Role</dt>
                    <dd>
                      {userProfile?.roleDescription ||
                        userProfile?.role ||
                        "—"}
                    </dd>
                  </div>

                  <div>
                    <dt>Usage Purpose</dt>
                    <dd>
                      {userProfile?.usagePurpose ||
                        userProfile?.usage_purpose ||
                        "—"}
                    </dd>
                  </div>

                  <div>
                    <dt>Member since</dt>
                    <dd>{formatDate(userProfile?.createdAt)}</dd>
                  </div>
                </dl>
              </>
            ) : (
              <div className="qfo-dash-edit-panel">
                <h2>Edit Profile</h2>
                <p>Update your profile details and profile picture.</p>

                <div className="qfo-dash-form-grid">
                  <label htmlFor="firstName">
                    First Name
                    <input
                      id="firstName"
                      type="text"
                      value={editForm.firstName}
                      onChange={(event) =>
                        handleFormChange("firstName", event.target.value)
                      }
                      placeholder="Enter first name"
                    />
                  </label>

                  <label htmlFor="lastName">
                    Last Name
                    <input
                      id="lastName"
                      type="text"
                      value={editForm.lastName}
                      onChange={(event) =>
                        handleFormChange("lastName", event.target.value)
                      }
                      placeholder="Enter last name"
                    />
                  </label>

                  <label htmlFor="username">
                    Username
                    <input
                      id="username"
                      type="text"
                      value={editForm.username}
                      onChange={(event) =>
                        handleFormChange("username", event.target.value)
                      }
                      placeholder="Enter username"
                    />
                  </label>

                  <label htmlFor="organisation">
                    Organisation
                    <input
                      id="organisation"
                      type="text"
                      value={editForm.organisation}
                      onChange={(event) =>
                        handleFormChange("organisation", event.target.value)
                      }
                      placeholder="Enter organisation"
                    />
                  </label>

                  <label htmlFor="role" className="qfo-dash-form-full-width">
                    What is your role?
                    <textarea
                      id="role"
                      value={editForm.role}
                      onChange={(event) =>
                        handleFormChange("role", event.target.value)
                      }
                      placeholder="Tell us about your role"
                      rows={3}
                    />
                  </label>

                  <label
                    htmlFor="usagePurpose"
                    className="qfo-dash-form-full-width"
                  >
                    How do you plan to use the Impact Alignment Map?
                    <textarea
                      id="usagePurpose"
                      value={editForm.usagePurpose}
                      onChange={(event) =>
                        handleFormChange("usagePurpose", event.target.value)
                      }
                      placeholder="Tell us how you plan to use the Impact Alignment Map"
                      rows={3}
                    />
                  </label>
                </div>

                {error && <div className="qfo-dash-error-message">{error}</div>}

                <div className="qfo-dash-form-actions">
                  <button
                    className="qfo-dash-primary-button"
                    onClick={handleSaveChanges}
                    disabled={updateLoading}
                  >
                    {updateLoading ? "Saving..." : "Save Changes"}
                  </button>

                  <button
                    className="qfo-dash-secondary-button"
                    onClick={handleEditToggle}
                    disabled={updateLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </section>

          <section className="qfo-dashboard-card qfo-dash-overview-card">
            <span className="qfo-dash-card-label">Workspace Summary</span>

            <div className="qfo-dash-metric-strip">
              <div>
                <span>Total projects</span>
                <strong>{projects.length}</strong>
              </div>

              <div>
                <span>Drafts</span>
                <strong>{dashboardStats.draftProjects.length}</strong>
              </div>

              <div>
                <span>Published</span>
                <strong>{dashboardStats.publishedProjects.length}</strong>
              </div>
            </div>

            {recentProjects.length > 0 ? (
              <div className="qfo-recent-projects">
                {recentProjects.map((project) => (
                  <button
                    type="button"
                    key={project.projectId}
                    className={`qfo-recent-project-row qfo-project-${normaliseProjectStatus(project.status)}`}
                    onClick={() => handleOpenProject(project.projectId)}
                  >
                    <span>
                      <strong>{project.projectName}</strong>
                      <small>{formatDateTime(getProjectDate(project))}</small>
                    </span>

                    <em>{formatProjectStatusLabel(project.status)}</em>
                  </button>
                ))}
              </div>
            ) : (
              <div className="qfo-dash-empty-box">
                <strong>No project activity yet</strong>
                <span>Create a project to start building your workspace history.</span>
              </div>
            )}
          </section>

          <section className="qfo-dashboard-card qfo-timeline-card">
            <span className="qfo-dash-card-label">Activity Timeline</span>

            <div className="qfo-dash-timeline">
              {timelineItems.length > 0 ? (
                timelineItems.map((item, index) => (
                  <div
                    key={`${item.title}-${index}`}
                    className={`qfo-dash-timeline-item ${item.tone}`}
                  >
                    <span />
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.description}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="qfo-dash-empty-box">
                  <strong>No recent activity yet</strong>
                  <span>Create or update a project to populate your timeline.</span>
                </div>
              )}
            </div>
          </section>
        </main>

        {showRefreshConfirm && (
          <ConfirmModal
            title="Discard unsaved changes?"
            message="Refreshing will discard edits you have not saved. Continue?"
            confirmText="Refresh anyway"
            cancelText="Keep editing"
            onConfirm={confirmProfileRefresh}
            onCancel={() => setShowRefreshConfirm(false)}
          />
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProfilePage;